/**
 * Shared chrome for the legal documents.
 *
 * Each document states its own "last updated" date. That date is meaningful —
 * customers rely on it to know whether terms have changed since they agreed
 * to them — so it is written into each page rather than generated at build
 * time, which would silently move every time the site was rebuilt.
 */
import type { ReactNode } from 'react';

export function LegalLayout({ title, updated, intro, children }: {
  title: string;
  updated: string;
  intro?: ReactNode;
  children: ReactNode;
}) {
  return (
    <article className="mx-auto w-full max-w-3xl px-5 sm:px-6 py-14 sm:py-20">
      <header className="pb-8 border-b border-line">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-muted">Last updated {updated}</p>
        {intro && <div className="mt-5 text-muted leading-relaxed">{intro}</div>}
      </header>
      <div className="mt-10 space-y-10">{children}</div>
    </article>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      <div className="space-y-3 text-muted leading-relaxed [&_a]:text-brand [&_a:hover]:underline">
        {children}
      </div>
    </section>
  );
}

export function List({ items }: { items: ReactNode[] }) {
  return (
    <ul className="space-y-2 pl-5 list-disc marker:text-faint">
      {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  );
}

export function Table({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-line">
      <table className="w-full text-sm min-w-[32rem]">
        <thead>
          <tr className="border-b border-line bg-raised">
            {head.map((h) => (
              <th key={h} className="text-left font-medium text-ink px-4 py-2.5">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-line last:border-0 align-top">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-muted">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/*
 * There was a <Todo> component here that highlighted values the operator had
 * still to supply — a registered address, an ICO number, a note to check the
 * sub-processor table. It was removed rather than left blank-by-default: these
 * are published documents, and a yellow "to be added" marker in the middle of
 * a data processing agreement reads to a customer as an unfinished contract,
 * whatever it was meant to say to us.
 *
 * The values that were genuinely missing now simply do not render. Fill in
 * company_address and company_number in the app_config tab and the contact
 * sections pick them up with no code change.
 */
